import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { FormatInt, FormatMoney } from '../../utils/format';
import { DASH, IsNum, Pill } from './campaignCardShared';
import { SortableHeader, useTableSort } from './useTableSort';

/**
 * Lista INLINE dos concorrentes de catálogo — substitui o antigo
 * `CompetitorsModal`, que já não exibia nada: ele lia `campaign_competition`,
 * tabela que guarda o payload do `price_to_win` (sem lista de ofertas e com
 * `competitors_count` zerado em todas as linhas).
 *
 * Os dados vêm da API do ML no momento em que a tabela abre — SEM cache, porque
 * preço de terceiro é o dado que envelhece mais rápido do monitor e esta tabela
 * é aberta justamente para decidir preço. Reabrir consulta de novo.
 *
 * Tudo o que o modal antigo mostrava está aqui — vendedor, reputação, loja
 * oficial, preço, frete, tipo de anúncio, garantia, localização — mais o que ele
 * não tinha: link para a oferta e para o perfil, quem de fato lidera (vindo do
 * `price_to_win`, não do "primeiro da lista"), e a comparação de boosts.
 */

const GRID_COLS = 'grid grid-cols-[minmax(0,2.2fr)_1fr_1fr_0.9fr_0.9fr] gap-2';

/** Quantas linhas por página. Catálogo popular passa de 70 ofertas. */
const PAGE_SIZE = 10;

const PAGER_BUTTON_CLASS =
    'rounded-md border border-mono-200 px-1.5 py-0.5 font-semibold transition-colors enabled:hover:border-mono-400 enabled:hover:text-black disabled:opacity-40 dark:border-mono-800 dark:enabled:hover:border-mono-600 dark:enabled:hover:text-white';

/**
 * O ML tem DUAS escalas de reputação, e misturá-las foi o que confundiu antes:
 * o ponto usava o termômetro (`level_id`, 1-5) enquanto o texto ao lado nomeava
 * a medalha (`power_seller_status`) — vendedor `3_yellow` sem medalha virava
 * ponto dourado ao lado de um "—".
 *
 * Agora cada escala tem seu lugar:
 *   · MEDALHA  → o ponto e o texto da coluna Reputação (a mesma coisa nomeada)
 *   · TERMÔMETRO → a cor do NOME do vendedor
 *
 * Assim as duas informações convivem sem competir pelo mesmo pixel.
 */
const MEDALS = {
    platinum: { rank: 3, label: 'Platinum', dot: 'bg-success-600' },
    gold: { rank: 2, label: 'Gold', dot: 'bg-warning-500' },
    silver: { rank: 1, label: 'Silver', dot: 'bg-mono-400 dark:bg-mono-500' },
};
const NO_MEDAL = { rank: 0, label: null, dot: 'bg-mono-200 dark:bg-mono-700' };

/** Termômetro do ML → cor do nome. Vermelho é o pior, verde o melhor. */
const LEVEL_TEXT_TONE = {
    '1_red': 'text-danger-600 dark:text-danger-400',
    '2_orange': 'text-warning-600 dark:text-warning-400',
    '3_yellow': 'text-warning-500 dark:text-warning-300',
    '4_light_green': 'text-success-500 dark:text-success-400',
    '5_green': 'text-success-700 dark:text-success-400',
};
/** Sem termômetro, o nome fica na cor padrão do texto. */
const NO_LEVEL_TONE = 'text-black dark:text-white';

const medalOf = (competitor) => MEDALS[competitor?.seller?.power_seller_status] || NO_MEDAL;

/**
 * Cor do nome do vendedor. `is_me` vence o termômetro: identificar a minha loja
 * na lista é mais urgente do que saber a reputação dela — que eu já conheço.
 */
const nameToneOf = (competitor) =>
    competitor?.is_me
        ? 'text-info-600 dark:text-info-400'
        : LEVEL_TEXT_TONE[competitor?.seller?.level_id] || NO_LEVEL_TONE;
const salesOf = (competitor) => Number(competitor?.seller?.transactions_total) || 0;

const COLUMNS = [
    { key: 'seller', label: 'Vendedor', align: 'left', title: 'Apelido do vendedor', value: (c) => c?.seller?.nickname || c?.seller_id || '' },
    { key: 'price', label: 'Preço', align: 'right', title: 'Preço praticado na oferta', value: (c) => c?.price },
    { key: 'shipping', label: 'Frete', align: 'right', title: 'Custo do frete (grátis = 0)', value: (c) => (c?.shipping?.free_shipping ? 0 : c?.shipping?.cost) },
    { key: 'reputation', label: 'Reputação', align: 'right', title: 'Medalha do vendedor (platinum > gold > silver)', value: (c) => medalOf(c).rank },
    { key: 'sales', label: 'Vendas', align: 'right', title: 'Total de transações do vendedor', value: (c) => c?.seller?.transactions_total },
];

/**
 * Ordem padrão — a que responde "com quem eu me pareço e quem devo observar":
 *
 *   1. o LÍDER      (é o alvo da disputa)
 *   2. EU           (a referência de comparação)
 *   3. lojas oficiais, das que mais vendem para as que menos vendem
 *   4. o resto, por medalha e, dentro da mesma medalha, por vendas
 *
 * Volume de vendas é o critério de desempate em toda parte: é o que separa
 * concorrente relevante de anúncio parado com preço baixo.
 */
function DefaultCompetitorOrder(rows) {
    const groupOf = (competitor) => {
        if (competitor?.is_winner) {
            return 0;
        }
        if (competitor?.is_me) {
            return 1;
        }
        return competitor?.official_store_id ? 2 : 3;
    };

    return rows.sort((a, b) => {
        const groupDelta = groupOf(a) - groupOf(b);
        if (groupDelta !== 0) {
            return groupDelta;
        }
        // Nas lojas oficiais o dono pediu vendas direto; no resto a medalha vem
        // antes, com vendas desempatando.
        if (groupOf(a) === 3) {
            const medalDelta = medalOf(b).rank - medalOf(a).rank;
            if (medalDelta !== 0) {
                return medalDelta;
            }
        }
        return salesOf(b) - salesOf(a);
    });
}

const BoostList = ({ boosts, label }) => {
    if (!Array.isArray(boosts) || boosts.length === 0) {
        return null;
    }
    return (
        <div className="flex flex-wrap items-center gap-1">
            <span className="text-[9px] uppercase tracking-wide text-mono-400">{label}</span>
            {boosts.map((boost) => (
                <span
                    key={boost.id}
                    title={boost.active ? 'Você já tem este impulso' : 'Oportunidade — ainda não tem'}
                    className={`rounded-md px-1 py-px text-[9px] font-semibold ${
                        boost.active
                            ? 'bg-success-soft text-success-text dark:bg-success-900/30 dark:text-success-300'
                            : 'bg-mono-100 text-mono-500 line-through dark:bg-mono-800 dark:text-mono-400'
                    }`}
                >
                    {boost.label}
                </span>
            ))}
        </div>
    );
};

const CampaignItemCompetitors = ({ itemId }) => {
    const [state, setState] = useState({ loading: true, data: null, error: null });
    const [page, setPage] = useState(1);
    const { sort, toggleSort, sortRows } = useTableSort(COLUMNS);

    // Reordenar com a página 4 aberta deixaria o usuário olhando o meio de uma
    // lista que acabou de mudar de significado.
    const changeSort = (key) => {
        setPage(1);
        toggleSort(key);
    };

    const load = useCallback(async () => {
        setState({ loading: true, data: null, error: null });
        try {
            const response = await api.getCompetitors(itemId);
            const payload = response?.data ?? response;
            setState({ loading: false, data: payload, error: null });
        } catch (error) {
            setState({
                loading: false,
                data: null,
                error: error?.message || 'Não foi possível carregar os concorrentes.',
            });
        }
    }, [itemId]);

    useEffect(() => {
        load();
    }, [load]);

    const competitors = useMemo(() => {
        const list = Array.isArray(state.data?.competitors) ? state.data.competitors : [];
        return sortRows(list, DefaultCompetitorOrder);
    }, [state.data, sortRows]);

    // Paginação: catálogo popular passa de 70 ofertas, e despejar tudo dentro de
    // um card do monitor empurra os cards seguintes para fora da tela.
    const totalPages = Math.max(1, Math.ceil(competitors.length / PAGE_SIZE));
    const currentPage = Math.min(page, totalPages);
    const pageRows = competitors.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    if (state.loading) {
        return <p className="px-1 py-2 text-[11px] text-mono-400">Consultando o catálogo no Mercado Livre…</p>;
    }
    if (state.error) {
        return <p className="px-1 py-2 text-[11px] text-danger-600 dark:text-danger-400">{state.error}</p>;
    }

    const data = state.data || {};
    if (!data.catalog_product_id) {
        return (
            <p className="px-1 py-2 text-[11px] text-mono-400">
                Anúncio sem produto de catálogo{data.reason?.length ? ` (${data.reason.join(', ')})` : ''}.
            </p>
        );
    }
    if (competitors.length === 0) {
        return <p className="px-1 py-2 text-[11px] text-mono-400">Nenhuma oferta concorrente encontrada.</p>;
    }

    return (
        <div className="overflow-x-auto">
            <div className="min-w-[620px]">
                {/* Resumo: o que responde "onde eu estou" antes de olhar linha a linha */}
                <div className="mb-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 px-1 text-[10px] text-mono-500 dark:text-mono-400">
                    <span>
                        <strong className="text-black dark:text-white">{FormatInt(data.total)}</strong> ofertas no
                        catálogo
                    </span>
                    {data.truncated ? (
                        <span title="A tabela traz as mais baratas; seu anúncio e o líder entram sempre, mesmo fora do topo.">
                            exibindo {FormatInt(data.returned)}
                        </span>
                    ) : null}
                    {IsNum(data.price_to_win) ? (
                        <span>
                            preço p/ ganhar{' '}
                            <strong className="text-black dark:text-white">{FormatMoney(data.price_to_win)}</strong>
                        </span>
                    ) : null}
                    {data.visit_share ? <span>participação nas visitas: {data.visit_share}</span> : null}
                </div>

                {Array.isArray(data.my_boosts) && data.my_boosts.length > 0 ? (
                    <div className="mb-1.5 px-1">
                        <BoostList boosts={data.my_boosts} label="Meus impulsos:" />
                    </div>
                ) : null}

                <SortableHeader columns={COLUMNS} sort={sort} onToggle={changeSort} gridClass={GRID_COLS} />

                {pageRows.map((competitor) => {
                    const tone = competitor.is_me
                        ? 'bg-info-soft/60 dark:bg-info-900/10'
                        : competitor.is_winner
                          ? 'bg-success-soft/60 dark:bg-success-900/10'
                          : '';
                    const medal = medalOf(competitor);

                    return (
                        <div
                            key={competitor.item_id}
                            className={`${GRID_COLS} items-center border-b border-mono-100 px-1 py-1 text-[11px] tabular-nums last:border-b-0 dark:border-mono-900 ${tone}`}
                        >
                            {/* Vendedor: apelido linkado ao perfil, oferta linkada ao anúncio */}
                            <div className="min-w-0">
                                <p className="flex min-w-0 items-center gap-1 truncate">
                                    {/* A minha loja não leva pílula: o nome em AZUL já
                                        me distingue, e a pílula "eu" competia por
                                        atenção com a de líder na mesma linha. Nos
                                        demais, a cor do nome é o TERMÔMETRO do ML. */}
                                    {competitor.seller?.permalink ? (
                                        <a
                                            href={competitor.seller.permalink}
                                            target="_blank"
                                            rel="noreferrer"
                                            title={
                                                competitor.is_me
                                                    ? 'Minha loja'
                                                    : `Abrir perfil do vendedor${competitor.seller.level_id ? ` · termômetro ${competitor.seller.level_id}` : ''}`
                                            }
                                            className={`truncate font-semibold hover:underline ${nameToneOf(competitor)}`}
                                        >
                                            {competitor.seller.nickname}
                                        </a>
                                    ) : (
                                        <span className={`truncate font-semibold ${nameToneOf(competitor)}`}>
                                            {competitor.seller?.nickname || `Vendedor ${competitor.seller_id || DASH}`}
                                        </span>
                                    )}
                                    {competitor.is_winner ? <Pill tone="success">líder</Pill> : null}
                                    {competitor.official_store_id ? <Pill tone="info">loja oficial</Pill> : null}
                                </p>
                                <p className="flex min-w-0 items-center gap-1.5 truncate text-[9px] text-mono-400">
                                    <a
                                        href={competitor.permalink}
                                        target="_blank"
                                        rel="noreferrer"
                                        title="Abrir o anúncio concorrente"
                                        className="font-mono hover:underline"
                                    >
                                        {competitor.item_id}
                                    </a>
                                    <span>{competitor.is_premium ? 'Premium' : 'Clássico'}</span>
                                    {competitor.seller_state ? (
                                        <span className="truncate">
                                            {competitor.seller_city ? `${competitor.seller_city}/` : ''}
                                            {competitor.seller_state}
                                        </span>
                                    ) : null}
                                    {competitor.warranty ? (
                                        <span className="truncate" title={`Garantia: ${competitor.warranty}`}>
                                            {competitor.warranty}
                                        </span>
                                    ) : null}
                                </p>
                            </div>

                            {/* Preço */}
                            <div className="text-right">
                                <p className="font-bold text-black dark:text-white">{FormatMoney(competitor.price)}</p>
                                {IsNum(competitor.original_price) &&
                                Number(competitor.original_price) > Number(competitor.price) ? (
                                    <p className="text-[9px] text-mono-400 line-through">
                                        {FormatMoney(competitor.original_price)}
                                    </p>
                                ) : null}
                            </div>

                            {/* Frete */}
                            <div className="text-right">
                                <p
                                    className={
                                        competitor.shipping?.free_shipping
                                            ? 'font-semibold text-success-600 dark:text-success-400'
                                            : 'text-black dark:text-white'
                                    }
                                >
                                    {competitor.shipping?.free_shipping
                                        ? 'Grátis'
                                        : IsNum(competitor.shipping?.cost)
                                          ? FormatMoney(competitor.shipping.cost)
                                          : DASH}
                                </p>
                                {competitor.shipping?.is_full ? (
                                    <p className="text-[9px] font-semibold uppercase text-info-600 dark:text-info-400">
                                        full
                                    </p>
                                ) : competitor.shipping?.logistic_type ? (
                                    <p className="text-[9px] uppercase text-mono-400">
                                        {competitor.shipping.logistic_type.replace(/_/g, ' ')}
                                    </p>
                                ) : null}
                            </div>

                            {/* Reputação: ponto e texto falam da MESMA coisa (a
                                medalha). Sem medalha = ponto neutro, nunca dourado. */}
                            <div
                                className="flex items-center justify-end gap-1"
                                title={medal.label ? `Medalha ${medal.label}` : 'Vendedor sem medalha'}
                            >
                                <span className={`inline-block h-2 w-2 shrink-0 rounded-full ${medal.dot}`} />
                                <span className="truncate text-[9px] uppercase text-mono-500">
                                    {medal.label || DASH}
                                </span>
                            </div>

                            {/* Vendas do vendedor */}
                            <div className="text-right text-mono-600 dark:text-mono-300">
                                {IsNum(competitor.seller?.transactions_total)
                                    ? FormatInt(competitor.seller.transactions_total)
                                    : DASH}
                            </div>
                        </div>
                    );
                })}

                {totalPages > 1 ? (
                    <div className="flex items-center justify-between gap-2 px-1 pt-1.5 text-[10px] text-mono-500 dark:text-mono-400">
                        <span>
                            {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, competitors.length)}{' '}
                            de {FormatInt(competitors.length)}
                        </span>
                        <span className="flex items-center gap-1">
                            <button
                                type="button"
                                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                                className={PAGER_BUTTON_CLASS}
                            >
                                Anterior
                            </button>
                            <span className="tabular-nums">
                                {currentPage}/{totalPages}
                            </span>
                            <button
                                type="button"
                                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages}
                                className={PAGER_BUTTON_CLASS}
                            >
                                Próxima
                            </button>
                        </span>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default CampaignItemCompetitors;
